import React, { useMemo } from 'react'
import Button from '@components/common/Button/Button.component'
import { Modal, Row, Col } from 'react-bootstrap'
import { statusIconMapping } from '@components/pages/Appointments/EventHeaderIcon/IconMapping'
import { ReactComponent as RecurringIcon } from '@assets/images/AppointmentIcons/recurring-event.svg'
import {
  AppointmentStatus,
  FormStatus,
  InsuranceStatus,
  OtherStatus,
  PatientStatus,
  PaymentStatus,
} from '@dataTypes/appointment.type'

type LegendInfoModalProps = {
  isOpen: boolean
  closeModal: () => void
}

const LegendInfoModal: React.FC<LegendInfoModalProps> = ({
  isOpen,
  closeModal,
}) => {
  const onModalClose = (): void => {
    closeModal()
  }

  const appointmentStatusLabels = useMemo(
    () => [
      { status: AppointmentStatus.Confirmed, label: 'Confirmed' },
      { status: AppointmentStatus.CheckIn, label: 'Check-in' },
      { status: AppointmentStatus.Completed, label: 'Completed' },
      { status: AppointmentStatus.Unconfirmed, label: 'Unconfirmed' },
      { status: AppointmentStatus.Alert, label: 'Alert' },
      { status: AppointmentStatus.FollowUp, label: 'Follow-up' },
      { status: AppointmentStatus.NewPatient, label: 'New patient' },
      { status: AppointmentStatus.Cancelled, label: 'Cancelled' },
      { status: AppointmentStatus.NoShow, label: 'No-show' },
      { status: AppointmentStatus.Late, label: 'Late' },
      { status: AppointmentStatus.InTreatment, label: 'In-treatment' },
      { status: AppointmentStatus.CheckOut, label: 'Check-out' },
    ],
    [],
  )

  const insuranceStatusLabels = useMemo(
    () => [
      { status: InsuranceStatus.InsuredVerify, label: 'Insured (Verified)' },
      { status: InsuranceStatus.NotInsured, label: 'Uninsured' },
      {
        status: InsuranceStatus.InsuredNotVerified,
        label: 'Insured (Unverified)',
      },
      { status: InsuranceStatus.InsuranceIssue, label: 'Review info' },
    ],
    [],
  )

  const patientStatusLabels = useMemo(
    () => [
      { status: PatientStatus.MedicalCondition, label: 'Medical condition' },
      {
        status: PatientStatus.CriticalAttentionNeeded,
        label: 'Attention required',
      },
      { status: PatientStatus.HasAllergy, label: 'Allergic' },
    ],
    [],
  )

  const formStatusLabels = useMemo(
    () => [
      { status: FormStatus.FormCompleted, label: 'Form (Completed)' },
      { status: FormStatus.FormIncomplete, label: 'Form (Incomplete)' },
    ],
    [],
  )

  const paymentStatusLabels = useMemo(
    () => [
      { status: PaymentStatus.MissedPayments, label: 'Missed payment(s)' },
    ],
    [],
  )

  const otherStatusLabels = useMemo(
    () => [{ status: OtherStatus.MedicalTest, label: 'Medical test' }],
    [],
  )

  return (
    <Modal
      show={isOpen}
      onHide={onModalClose}
      size="sm"
      className="legend-info-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="bg-custom3 mb-3" closeButton>
        <Modal.Title>Icon information</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mt-2">
          <Col className="col-md-3">
            <span className="info-item-title">Appointment status</span>
            <ul className="info-item-list">
              {appointmentStatusLabels.map(({ status, label }) => {
                const IconComponent =
                  statusIconMapping['appointment_status'](status)
                return IconComponent ? (
                  <li key={status}>
                    <div className="info-item-list-item">
                      <span key={status} className="icon-wrapper">
                        {IconComponent}
                      </span>
                      <span className="text">{label}</span>
                    </div>
                  </li>
                ) : null
              })}
            </ul>
          </Col>
          <Col className="col-md-3">
            <span className="info-item-title">Insurance status</span>
            <ul className="info-item-list">
              {insuranceStatusLabels.map(({ status, label }) => {
                const IconComponent =
                  statusIconMapping['insurance_status'](status)
                return IconComponent ? (
                  <li key={status}>
                    <div className="info-item-list-item">
                      <span key={status} className="icon-wrapper">
                        {IconComponent}
                      </span>
                      <span className="text">{label}</span>
                    </div>
                  </li>
                ) : null
              })}
            </ul>
          </Col>
          <Col className="col-md-3">
            <span className="info-item-title">Patient status</span>
            <ul className="info-item-list">
              {patientStatusLabels.map(({ status, label }) => {
                const IconComponent =
                  statusIconMapping['patient_status'](status)
                return IconComponent ? (
                  <li key={status}>
                    <div className="info-item-list-item">
                      <span key={status} className="icon-wrapper">
                        {IconComponent}
                      </span>
                      <span className="text">{label}</span>
                    </div>
                  </li>
                ) : null
              })}
            </ul>
          </Col>
          <Col className="col-md-3">
            <span className="info-item-title">Other</span>
            <ul className="info-item-list">
              {formStatusLabels.map(({ status, label }) => {
                const IconComponent = statusIconMapping['form_status'](status)
                return IconComponent ? (
                  <li key={status}>
                    <div className="info-item-list-item">
                      <span key={status} className="icon-wrapper">
                        {IconComponent}
                      </span>
                      <span className="text">{label}</span>
                    </div>
                  </li>
                ) : null
              })}
              {paymentStatusLabels.map(({ status, label }) => {
                const IconComponent =
                  statusIconMapping['payment_status'](status)
                return IconComponent ? (
                  <li key={status}>
                    <div className="info-item-list-item">
                      <span key={status} className="icon-wrapper">
                        {IconComponent}
                      </span>
                      <span className="text">{label}</span>
                    </div>
                  </li>
                ) : null
              })}
              {otherStatusLabels.map(({ status, label }) => {
                const IconComponent = statusIconMapping['other_status'](status)
                return IconComponent ? (
                  <li key={status}>
                    <div className="info-item-list-item">
                      <span key={status} className="icon-wrapper">
                        {IconComponent}
                      </span>
                      <span className="text">{label}</span>
                    </div>
                  </li>
                ) : null
              })}
              <li>
                <div className="info-item-list-item">
                  <span className="icon-wrapper recurring-icon">
                    <RecurringIcon />
                  </span>
                  <span className="text">Recurring</span>
                </div>
              </li>
            </ul>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col></Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button
          size="md"
          className="button-primary close-button"
          onClick={onModalClose}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default LegendInfoModal
